export const LANGUAGES = [
  {
    code: 'en',
    label: 'English',
    nativeLabel: 'English',
    locale: 'en-US',
    dir: 'ltr',
  },
  {
    code: 'cs',
    label: 'Czech',
    nativeLabel: 'Čeština',
    locale: 'cs-CZ',
    dir: 'ltr',
  },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

export const en = {
  'app.loading': 'Loading',

  'common.admin': 'Admin',
  'common.all': 'All',
  'common.back': 'Back',
  'common.basic': 'Basic',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.complete': 'Complete',
  'common.copied': '{{item}} copied',
  'common.devMode': 'Dev mode',
  'common.edit': 'Edit',
  'common.exchangeFallback': 'Exchange',
  'common.logoAlt': '{{name}} logo',
  'common.next': 'Next',
  'common.private': 'Private',
  'common.route': 'route',
  'common.routeOne': 'route',
  'common.routeFew': 'routes',
  'common.routeOther': 'routes',
  'common.routes': 'routes',
  'common.round': 'round',
  'common.roundOne': 'round',
  'common.roundFew': 'rounds',
  'common.roundOther': 'rounds',
  'common.rounds': 'rounds',
  'common.save': 'Save',
  'common.submit': 'Submit',
  'common.submitting': 'Submitting...',
  'common.unknown': 'Unknown',
  'common.uploading': 'Uploading...',

  'status.active': 'Active',
  'status.activeLower': 'active',
  'status.inactive': 'Inactive',
  'status.inactiveLower': 'inactive',
  'status.pending': 'Pending',
  'status.rejected': 'Rejected',
  'status.verified': 'Verified',

  'language.current': 'Current language',
  'language.czech': 'Czech',
  'language.english': 'English',
  'language.openSelector': 'Choose language',
  'language.title': 'Language',

  'nav.admin': 'Admin',
  'nav.calculator': 'Calculator',
  'nav.pairs': 'Routes',
  'nav.profile': 'Profile',

  'announcements.empty': 'No announcements yet.',
  'announcements.title': 'Announcements',
  'announcements.unread': '{{count}} unread',
  'announcements.unreadAria': '{{count}} unread announcements',

  'pairs.eyebrow': 'Scanner',
  'pairs.title': 'Flipping Routes',
  'pairs.searchCoin': 'Search coin',
  'pairs.exchange': 'Exchange',
  'pairs.type': 'Type',
  'pairs.typeSingle': 'Single',
  'pairs.typeDouble': 'Double',
  'pairs.status': 'Status',
  'pairs.sort': 'Sort',
  'pairs.sortDefault': 'Default',
  'pairs.sortSpreadHigh': 'Spread high',
  'pairs.sortSpreadLow': 'Spread low',
  'pairs.failedToLoad': 'Failed to load pairs',
  'pairs.noneFound': 'No pairs found matching your criteria',
  'pairs.sectionBasic': 'Basic',
  'pairs.sectionPrivate': 'Private',
  'pairs.routeCount': '{{count}} {{routeWord}}',
  'pairs.filteredRouteCount': '{{count}} {{status}} {{routeWord}}',
  'pairs.sectionCount': '{{count}} {{routeWord}}',

  'pair.address': 'Address',
  'pair.addPersonalNote': 'Add a personal note',
  'pair.activeSignal': 'Active signal',
  'pair.closeDetails': 'Collapse details',
  'pair.closeNoteEditor': 'Close note editor',
  'pair.cooldownActive': 'Cooldown active',
  'pair.copyAddress': 'Copy address',
  'pair.copyMemo': 'Copy memo',
  'pair.disableNotifications': 'Disable bot notifications',
  'pair.editNote': 'Edit note',
  'pair.enableNotifications': 'Enable bot notifications',
  'pair.expandDetails': 'Expand details',
  'pair.logRound': 'Log round',
  'pair.memo': 'Memo',
  'pair.na': 'N/A',
  'pair.network': 'Network',
  'pair.noNoteAdded': 'No note added',
  'pair.notes': 'Notes',
  'pair.personalNote': 'Personal note',
  'pair.privateRoute': 'Private route',
  'pair.safetySignal': 'Profitability signal',
  'pair.transferDetails': 'Transfer details',
  'pair.transferDetailsMissing': 'Transfer details are not configured for this pair yet.',

  'calculator.eyebrow': 'Calculator',
  'calculator.title': 'Flipping Rounds',
  'calculator.amountInvested': 'Amount invested',
  'calculator.amountReceived': 'Amount received',
  'calculator.averageProfit': 'Avg profit',
  'calculator.budget': 'Budget',
  'calculator.coin': 'Coin',
  'calculator.coinPlaceholder': 'BTC, ETH, USDT...',
  'calculator.deleteRound': 'Delete round',
  'calculator.exchangeBuy': 'Exchange buy',
  'calculator.exchangeBuyPlaceholder': 'binance',
  'calculator.exchangeSell': 'Exchange sell',
  'calculator.exchangeSellPlaceholder': 'bybit',
  'calculator.exportCsv': 'Export CSV',
  'calculator.exportFilePrefix': 'flipping_rounds',
  'calculator.last30Days': 'Last 30 days',
  'calculator.logRound': 'Log round',
  'calculator.monthlyGoal': 'Monthly goal',
  'calculator.monthlyGoalPlaceholder': 'Monthly goal',
  'calculator.noMonthlyGoal': 'No monthly goal set',
  'calculator.noRounds': 'No rounds yet.',
  'calculator.notes': 'Notes',
  'calculator.profit': 'Profit',
  'calculator.projected': '{{amount}} projected ({{percent}}%)',
  'calculator.progressComplete': '{{percent}}% complete',
  'calculator.recentLogged': '{{count}} logged',
  'calculator.recentRounds': 'Recent rounds',
  'calculator.totalProfit': 'Total profit',
  'calculator.totalRounds': 'Total rounds',

  'profile.eyebrow': 'Account',
  'profile.title': 'Profile',
  'profile.access': 'Access: {{level}}',
  'profile.addExchange': 'Add exchange',
  'profile.addExchangeVerification': 'Add exchange verification',
  'profile.addFirstExchange': 'Add first exchange',
  'profile.budget': 'Budget',
  'profile.chooseNewScreenshot': 'Choose new screenshot',
  'profile.chooseScreenshot': 'Choose screenshot',
  'profile.enterUidAndScreenshot': 'Enter your exchange UID and upload a screenshot for admin review.',
  'profile.exchange': 'Exchange',
  'profile.exchangeUid': 'Exchange UID',
  'profile.exchangeUidPlaceholder': 'Your exchange user ID',
  'profile.exchangeVerifications': 'Exchange verifications',
  'profile.noExchanges': 'No exchanges verified yet.',
  'profile.privateAccess': 'Private access',
  'profile.privateAccessHint': 'Unlock private flipping routes with a one-time $5,000 access fee.',
  'profile.requestPrivateAccess': 'Request access',
  'profile.privateAccessRequestTitle': 'Request Private access',
  'profile.privateAccessRequestBody':
    'Applications are currently processed manually. Contact manager to request Private access.',
  'profile.contactManager': 'Contact manager',
  'profile.reason': 'Reason: {{reason}}',
  'profile.retryVerification': 'Retry',
  'profile.retryVerificationError': 'Failed to retry verification. Please try again.',
  'profile.retryVerificationHint':
    'Update your UID if needed and upload a new screenshot. The verification will return to Pending for admin review.',
  'profile.retryVerificationTitle': 'Retry {{exchange}} verification',
  'profile.screenshotMustShowUid': 'Screenshot must show your exchange UID.',
  'profile.screenshotUploaded': 'Screenshot uploaded',
  'profile.submitExchangeError': 'Failed to submit exchange verification. Please try again.',
  'profile.uid': 'UID: {{uid}}',
  'profile.uploadScreenshot': 'Upload screenshot',
  'profile.verifiedExchanges': 'Verified exchanges',

  'onboarding.eyebrow': 'Setup',
  'onboarding.mockEyebrow': 'Mock setup',
  'onboarding.title': 'Crypto Flipping Scanner',
  'onboarding.stepFaq': 'FAQ',
  'onboarding.stepExchanges': 'Exchanges',
  'onboarding.stepVerification': 'Verification',
  'onboarding.faqArbitrageQuestion': 'What is crypto flipping?',
  'onboarding.faqArbitrageAnswer':
    'Crypto flipping means moving through a buy/sell route to capture price or rate differences between exchanges.',
  'onboarding.faqStartQuestion': 'How do I start?',
  'onboarding.faqStartAnswer':
    'Verify accounts on the exchanges you use. After review, matching flipping routes will appear in the scanner.',
  'onboarding.faqAccessQuestion': 'What is Basic vs Private access?',
  'onboarding.faqAccessAnswer':
    'Basic access shows common pairs. Private access unlocks higher-signal pairs after a one-time $5,000 access fee and manual approval.',
  'onboarding.faqRoundsQuestion': 'What are rounds?',
  'onboarding.faqRoundsAnswer':
    'A round is one complete flipping sequence: buy, sell, and record the result in the calculator.',
  'onboarding.selectExchanges': 'Select exchanges',
  'onboarding.selectExchangesHint': 'Pick at least one exchange for verification.',
  'onboarding.verifyAccounts': 'Verify accounts',
  'onboarding.verifyAccountsHint': 'Submit each exchange UID, then upload the matching screenshot.',
  'onboarding.enterUid': 'Enter your UID',
  'onboarding.uid': 'UID: {{uid}}',
  'onboarding.screenshotUploaded': 'Screenshot uploaded.',
  'onboarding.mockNotice': 'Test route: UID submit, screenshot upload, and completion are simulated locally.',
  'onboarding.mockComplete':
    'Mock onboarding completed. No user, verification, screenshot, or onboarding records were written.',
  'onboarding.openApp': 'Open app',
  'onboarding.restartMock': 'Restart mock onboarding',
  'onboarding.uploadScreenshot': 'Upload screenshot',
} as const;

export type TranslationKey = keyof typeof en;
type TranslationCatalog = Record<TranslationKey, string>;

export const cs = {
  'app.loading': 'Načítání',

  'common.admin': 'Admin',
  'common.all': 'Vše',
  'common.back': 'Zpět',
  'common.basic': 'Basic',
  'common.cancel': 'Zrušit',
  'common.close': 'Zavřít',
  'common.complete': 'Dokončit',
  'common.copied': 'Zkopírováno: {{item}}',
  'common.devMode': 'Dev mode',
  'common.edit': 'Upravit',
  'common.exchangeFallback': 'Burza',
  'common.logoAlt': 'Logo: {{name}}',
  'common.next': 'Další',
  'common.private': 'Private',
  'common.route': 'trasa',
  'common.routeOne': 'trasa',
  'common.routeFew': 'trasy',
  'common.routeOther': 'tras',
  'common.routes': 'trasy',
  'common.round': 'kolo',
  'common.roundOne': 'kolo',
  'common.roundFew': 'kola',
  'common.roundOther': 'kol',
  'common.rounds': 'kola',
  'common.save': 'Uložit',
  'common.submit': 'Odeslat',
  'common.submitting': 'Odesílání...',
  'common.unknown': 'Neznámé',
  'common.uploading': 'Nahrávání...',

  'status.active': 'Aktivní',
  'status.activeLower': 'aktivních',
  'status.inactive': 'Neaktivní',
  'status.inactiveLower': 'neaktivních',
  'status.pending': 'Čeká',
  'status.rejected': 'Zamítnuto',
  'status.verified': 'Ověřeno',

  'language.current': 'Aktuální jazyk',
  'language.czech': 'Čeština',
  'language.english': 'Angličtina',
  'language.openSelector': 'Vybrat jazyk',
  'language.title': 'Jazyk',

  'nav.admin': 'Admin',
  'nav.calculator': 'Kalkulačka',
  'nav.pairs': 'Trasy',
  'nav.profile': 'Profil',

  'announcements.empty': 'Žádná oznámení.',
  'announcements.title': 'Oznámení',
  'announcements.unread': '{{count}}× nepřečteno',
  'announcements.unreadAria': 'Nepřečtená oznámení: {{count}}',

  'pairs.eyebrow': 'Scanner',
  'pairs.title': 'Flipping trasy',
  'pairs.searchCoin': 'Hledat coin',
  'pairs.exchange': 'Burza',
  'pairs.type': 'Typ',
  'pairs.typeSingle': 'Jedna měna',
  'pairs.typeDouble': 'Dvě měny',
  'pairs.status': 'Stav',
  'pairs.sort': 'Řazení',
  'pairs.sortDefault': 'Výchozí',
  'pairs.sortSpreadHigh': 'Nejvyšší spread',
  'pairs.sortSpreadLow': 'Nejnižší spread',
  'pairs.failedToLoad': 'Páry se nepodařilo načíst',
  'pairs.noneFound': 'Žádné páry neodpovídají filtrům',
  'pairs.sectionBasic': 'Basic',
  'pairs.sectionPrivate': 'Private',
  'pairs.routeCount': '{{count}} {{routeWord}}',
  'pairs.filteredRouteCount': '{{count}} {{status}} {{routeWord}}',
  'pairs.sectionCount': '{{count}} {{routeWord}}',

  'pair.address': 'Adresa',
  'pair.addPersonalNote': 'Přidat osobní poznámku',
  'pair.activeSignal': 'Aktivní signál',
  'pair.closeDetails': 'Sbalit detaily',
  'pair.closeNoteEditor': 'Zavřít editor poznámky',
  'pair.cooldownActive': 'Aktivní cooldown',
  'pair.copyAddress': 'Kopírovat adresu',
  'pair.copyMemo': 'Kopírovat memo',
  'pair.disableNotifications': 'Vypnout bot notifikace',
  'pair.editNote': 'Upravit poznámku',
  'pair.enableNotifications': 'Zapnout bot notifikace',
  'pair.expandDetails': 'Rozbalit detaily',
  'pair.logRound': 'Zapsat kolo',
  'pair.memo': 'Memo',
  'pair.na': 'N/A',
  'pair.network': 'Síť',
  'pair.noNoteAdded': 'Žádná poznámka',
  'pair.notes': 'Poznámky',
  'pair.personalNote': 'Osobní poznámka',
  'pair.privateRoute': 'Private trasa',
  'pair.safetySignal': 'Signál ziskovosti',
  'pair.transferDetails': 'Údaje pro převod',
  'pair.transferDetailsMissing': 'Údaje pro převod zatím nejsou pro tento pár nastavené.',

  'calculator.eyebrow': 'Kalkulačka',
  'calculator.title': 'Kola flippingu',
  'calculator.amountInvested': 'Vložená částka',
  'calculator.amountReceived': 'Přijatá částka',
  'calculator.averageProfit': 'Prům. zisk',
  'calculator.budget': 'Budget',
  'calculator.coin': 'Coin',
  'calculator.coinPlaceholder': 'BTC, ETH, USDT...',
  'calculator.deleteRound': 'Smazat kolo',
  'calculator.exchangeBuy': 'Nákupní burza',
  'calculator.exchangeBuyPlaceholder': 'binance',
  'calculator.exchangeSell': 'Prodejní burza',
  'calculator.exchangeSellPlaceholder': 'bybit',
  'calculator.exportCsv': 'Export CSV',
  'calculator.exportFilePrefix': 'kola_flippingu',
  'calculator.last30Days': 'Posledních 30 dní',
  'calculator.logRound': 'Zapsat kolo',
  'calculator.monthlyGoal': 'Měsíční cíl',
  'calculator.monthlyGoalPlaceholder': 'Měsíční cíl',
  'calculator.noMonthlyGoal': 'Měsíční cíl není nastavený',
  'calculator.noRounds': 'Zatím žádná kola.',
  'calculator.notes': 'Poznámky',
  'calculator.profit': 'Zisk',
  'calculator.projected': 'Odhad {{amount}} ({{percent}} %)',
  'calculator.progressComplete': 'Splněno {{percent}} %',
  'calculator.recentLogged': 'Zapsáno: {{count}}',
  'calculator.recentRounds': 'Nedávná kola',
  'calculator.totalProfit': 'Celkový zisk',
  'calculator.totalRounds': 'Celkem kol',

  'profile.eyebrow': 'Účet',
  'profile.title': 'Profil',
  'profile.access': 'Přístup: {{level}}',
  'profile.addExchange': 'Přidat burzu',
  'profile.addExchangeVerification': 'Přidat ověření burzy',
  'profile.addFirstExchange': 'Přidat první burzu',
  'profile.budget': 'Budget',
  'profile.chooseNewScreenshot': 'Vybrat nový screenshot',
  'profile.chooseScreenshot': 'Vybrat screenshot',
  'profile.enterUidAndScreenshot': 'Zadejte UID z burzy a nahrajte screenshot pro kontrolu adminem.',
  'profile.exchange': 'Burza',
  'profile.exchangeUid': 'UID burzy',
  'profile.exchangeUidPlaceholder': 'Vaše uživatelské ID na burze',
  'profile.exchangeVerifications': 'Ověření burz',
  'profile.noExchanges': 'Zatím nemáte ověřenou žádnou burzu.',
  'profile.privateAccess': 'Private přístup',
  'profile.privateAccessHint': 'Odemkněte Private trasy pro flipping jednorázovým poplatkem 5 000 USD.',
  'profile.requestPrivateAccess': 'Pozadat o pristup',
  'profile.privateAccessRequestTitle': 'Pozadat o Private pristup',
  'profile.privateAccessRequestBody':
    'Zadosti se aktualne zpracovavaji manualne. Kontaktujte managera pro zadost o Private pristup.',
  'profile.contactManager': 'Kontaktovat managera',
  'profile.reason': 'Důvod: {{reason}}',
  'profile.retryVerification': 'Zkusit znovu',
  'profile.retryVerificationError': 'Ověření se nepodařilo odeslat znovu. Zkuste to prosím znovu.',
  'profile.retryVerificationHint':
    'V případě potřeby upravte UID a nahrajte nový screenshot. Ověření se vrátí do stavu Čeká na kontrolu adminem.',
  'profile.retryVerificationTitle': 'Znovu ověřit {{exchange}}',
  'profile.screenshotMustShowUid': 'Screenshot musí ukazovat vaše UID na burze.',
  'profile.screenshotUploaded': 'Screenshot nahrán',
  'profile.submitExchangeError': 'Ověření burzy se nepodařilo odeslat. Zkuste to prosím znovu.',
  'profile.uid': 'UID: {{uid}}',
  'profile.uploadScreenshot': 'Nahrát screenshot',
  'profile.verifiedExchanges': 'Ověřené burzy',

  'onboarding.eyebrow': 'Nastavení',
  'onboarding.mockEyebrow': 'Mock nastavení',
  'onboarding.title': 'Crypto Flipping Scanner',
  'onboarding.stepFaq': 'FAQ',
  'onboarding.stepExchanges': 'Burzy',
  'onboarding.stepVerification': 'Ověření',
  'onboarding.faqArbitrageQuestion': 'Co je crypto flipping?',
  'onboarding.faqArbitrageAnswer':
    'Crypto flipping znamená projít nákupní a prodejní trasou tak, aby se využil rozdíl v ceně nebo kurzu mezi burzami.',
  'onboarding.faqStartQuestion': 'Jak začít?',
  'onboarding.faqStartAnswer':
    'Ověřte účty na burzách, které používáte. Po kontrole se ve scanneru zobrazí odpovídající trasy pro flipping.',
  'onboarding.faqAccessQuestion': 'Jaký je rozdíl mezi Basic a Private přístupem?',
  'onboarding.faqAccessAnswer':
    'Basic zobrazuje bezne pary. Private odemkne kvalitnejsi signaly po jednorazovem poplatku 5 000 USD a manualnim schvaleni.',
  'onboarding.faqRoundsQuestion': 'Co je kolo flippingu?',
  'onboarding.faqRoundsAnswer':
    'Kolo flippingu je jedna kompletní sekvence: nákup, prodej a zapsání výsledku do kalkulačky.',
  'onboarding.selectExchanges': 'Vyberte burzy',
  'onboarding.selectExchangesHint': 'Pro ověření vyberte alespoň jednu burzu.',
  'onboarding.verifyAccounts': 'Ověřte účty',
  'onboarding.verifyAccountsHint': 'Odešlete UID pro každou burzu a poté nahrajte odpovídající screenshot.',
  'onboarding.enterUid': 'Zadejte UID',
  'onboarding.uid': 'UID: {{uid}}',
  'onboarding.screenshotUploaded': 'Screenshot nahrán.',
  'onboarding.mockNotice': 'Testovací route: odeslání UID, nahrání screenshotu a dokončení se simulují lokálně.',
  'onboarding.mockComplete':
    'Mock onboarding dokončen. Do databáze se nezapsal žádný uživatel, ověření, screenshot ani stav onboardingu.',
  'onboarding.openApp': 'Otevřít aplikaci',
  'onboarding.restartMock': 'Restartovat mock onboarding',
  'onboarding.uploadScreenshot': 'Nahrát screenshot',
} satisfies TranslationCatalog;

export const TRANSLATIONS: Record<LanguageCode, TranslationCatalog> = {
  en,
  cs,
};
